import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { GOOGLE_REVIEWS_URL } from "@/data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n = 5, size = 18 }) => (
  <div className="flex justify-center gap-1">
    {[...Array(5)].map((_, s) => (
      <Star key={s} size={size} className={s < Math.round(n) ? "text-gold fill-gold" : "text-espresso/20"} />
    ))}
  </div>
);

export const Testimonials = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setData(r.data)).catch(() => setFailed(true));
  }, []);

  const reviews = data?.reviews || [];
  const t = reviews[i];
  const reviewsUrl = data?.reviewsUri || GOOGLE_REVIEWS_URL;

  return (
    <section id="reviews" data-testid="testimonials-section" className="bg-beige/40 py-24 md:py-36">
      <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
        <SectionHeading chapter="11" eyebrow="Google Reviews" title="Loved by Our" script="customers." align="center" />

        {data && !failed && (
          <Reveal className="mt-10">
            <div data-testid="google-rating-summary" className="inline-flex items-center gap-5 bg-espresso text-cream px-8 py-5">
              <span className="font-serif text-5xl text-gold leading-none">{Number(data.rating).toFixed(1)}</span>
              <span className="text-left">
                <Stars n={data.rating} size={15} />
                <span className="block text-[11px] tracking-[0.2em] uppercase text-cream/60 mt-1.5">
                  {data.ratingCount} reviews on Google
                </span>
              </span>
            </div>
          </Reveal>
        )}

        <Reveal className="mt-10 relative">
          {failed ? (
            <div className="border gold-hairline bg-cream px-8 py-14" data-testid="reviews-unavailable">
              <p className="font-serif text-2xl text-espresso">Our latest reviews live on Google.</p>
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-block border border-espresso text-espresso px-7 py-3.5 text-xs tracking-[0.25em] uppercase hover:bg-espresso hover:text-gold transition-colors">
                Read Our Reviews
              </a>
            </div>
          ) : !data ? (
            <div className="border gold-hairline bg-cream px-8 py-16 animate-pulse" data-testid="reviews-loading">
              <div className="h-5 w-44 bg-espresso/10 mx-auto mb-4" />
              <div className="h-4 w-72 max-w-full bg-espresso/10 mx-auto" />
            </div>
          ) : (
            <>
              <div className="border gold-hairline bg-cream px-8 md:px-16 py-14">
                <AnimatePresence mode="wait">
                  <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}>
                    <div data-testid="testimonial-stars"><Stars n={t?.rating} /></div>
                    <p className="mt-6 font-serif text-xl md:text-2xl text-espresso leading-relaxed" data-testid="testimonial-text">“{t?.text}”</p>
                    <p className="mt-6 text-sm tracking-[0.2em] uppercase text-espresso/70" data-testid="testimonial-name">{t?.author}</p>
                    <p className="text-xs text-gold-dark tracking-[0.15em] uppercase mt-1" data-testid="testimonial-meta">
                      {t?.relativeTime} · Posted on Google
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {reviews.length > 1 && (
                <div className="mt-8 flex items-center justify-center gap-6">
                  <button data-testid="testimonial-prev" onClick={() => setI((i - 1 + reviews.length) % reviews.length)} aria-label="Previous review" className="border border-espresso/20 p-3 text-espresso/60 hover:border-gold hover:text-gold-dark transition-colors"><ChevronLeft size={18} /></button>
                  <span className="text-xs tracking-[0.3em] text-espresso/50">{i + 1} / {reviews.length}</span>
                  <button data-testid="testimonial-next" onClick={() => setI((i + 1) % reviews.length)} aria-label="Next review" className="border border-espresso/20 p-3 text-espresso/60 hover:border-gold hover:text-gold-dark transition-colors"><ChevronRight size={18} /></button>
                </div>
              )}

              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <a
                  data-testid="read-reviews-btn"
                  href={reviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-espresso text-espresso px-7 py-3.5 text-xs tracking-[0.25em] uppercase hover:bg-espresso hover:text-gold transition-colors"
                >
                  Read Our Reviews
                </a>
                <a
                  data-testid="share-review-btn"
                  href={GOOGLE_REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gold-dark hover:text-espresso px-4 py-3.5 text-xs tracking-[0.25em] uppercase transition-colors"
                >
                  <Star size={15} /> Share Your Experience on Google
                </a>
              </div>
              <p className="mt-6 text-[10px] tracking-[0.15em] uppercase text-espresso/40" data-testid="reviews-attribution">
                Reviews from Google Maps · user-generated content, shown as posted
              </p>
            </>
          )}
        </Reveal>
      </div>
    </section>
  );
};

